import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchSiteDataWithFallback, normalizePackagesList } from '../api/client';
import { sanitizeStoredLinks, sanitizeStoredContact, sanitizeSeoSettings } from '../config/siteConfig';
import { sanitizeNotificationsDoc } from '../config/notifications';

const ContentContext = createContext();

function applySitePayload(data, setters) {
  const {
    setHomeContent,
    setAboutContent,
    setContactInfo,
    setSeoSettings,
    setLinks,
    setPackages,
    setNotifications
  } = setters;

  if (data.homeContent) setHomeContent(data.homeContent);
  if (data.aboutContent) setAboutContent(data.aboutContent);
  if (data.contactInfo) setContactInfo(sanitizeStoredContact(data.contactInfo));
  if (data.seoSettings) setSeoSettings(sanitizeSeoSettings(data.seoSettings));
  if (data.links) setLinks(sanitizeStoredLinks(data.links));
  if (data.notifications) setNotifications(sanitizeNotificationsDoc(data.notifications));
  setPackages(normalizePackagesList(data.packages));
}

function hasLoadedContent(state) {
  return Boolean(
    state.packages?.length ||
      state.homeContent ||
      state.aboutContent ||
      state.contactInfo ||
      state.seoSettings ||
      state.links ||
      state.notifications?.items?.length
  );
}

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    return {
      homeContent: null,
      aboutContent: null,
      contactInfo: null,
      seoSettings: null,
      links: null,
      packages: [],
      notifications: null,
      loading: false,
      error: null,
      dataSource: null
    };
  }
  return context;
};

export const ContentProvider = ({ children }) => {
  const [homeContent, setHomeContent] = useState(null);
  const [aboutContent, setAboutContent] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [seoSettings, setSeoSettings] = useState(null);
  const [links, setLinks] = useState(null);
  const [packages, setPackages] = useState([]);
  const [notifications, setNotifications] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState(null);

  useEffect(() => {
    loadAllContent();
    const onRefresh = () => loadAllContent();
    window.addEventListener('tonrex:site-refresh', onRefresh);
    return () => window.removeEventListener('tonrex:site-refresh', onRefresh);
  }, []);

  const loadAllContent = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, source } = await fetchSiteDataWithFallback();
      setDataSource(source);

      applySitePayload(data, {
        setHomeContent,
        setAboutContent,
        setContactInfo,
        setSeoSettings,
        setLinks,
        setPackages,
        setNotifications
      });

      const loaded = hasLoadedContent({
        packages: normalizePackagesList(data.packages),
        homeContent: data.homeContent,
        aboutContent: data.aboutContent,
        contactInfo: data.contactInfo,
        seoSettings: data.seoSettings,
        links: data.links,
        notifications: data.notifications
      });

      if (!loaded) {
        setError('Site verisi bulunamadı. build/data/site.json sunucuya yüklendi mi kontrol edin.');
        console.warn('[Tonrex] Site verisi yok — source:', source);
      } else if (source === 'static' || source === 'partial') {
        console.info('[Tonrex] İçerik statik JSON dosyalarından yüklendi.');
      }
    } catch (err) {
      console.error('Load content error:', err);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    homeContent,
    aboutContent,
    contactInfo,
    seoSettings,
    links,
    packages,
    notifications,
    loading,
    error,
    dataSource,
    refresh: loadAllContent
  };

  const isAdminRoute =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

  return (
    <ContentContext.Provider value={value}>
      {error && isAdminRoute ? (
        <div
          role="status"
          style={{
            background: '#1a0508',
            color: '#c9a0a6',
            borderBottom: '1px solid rgba(92, 28, 42, 0.35)',
            padding: '10px 16px',
            textAlign: 'center',
            fontSize: '14px',
            zIndex: 9999
          }}
        >
          {error}
        </div>
      ) : null}
      {children}
    </ContentContext.Provider>
  );
};

export { sanitizeStoredLinks, sanitizeStoredContact, sanitizeSeoSettings } from '../config/siteConfig';
